/**
 * Generic lazy icon factory with preload capabilities
 * Follows React 19 best practices to prevent network waterfalls
 *
 * @module createLazyIcon
 */
import {lazy, ComponentType, SVGProps} from 'react';

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface LazyIconEntry {
  Component: IconComponent;
  preload: () => Promise<{default: IconComponent}>;
}

/**
 * Creates a lazy-loaded icon component with preload capability
 * Prevents duplicate preload requests using memoization
 *
 * @param importFn - Dynamic import function that returns the icon component
 * @returns Object containing the lazy Component and preload function
 *
 * @example
 * ```typescript
 * const chartIcon = createLazyIcon(() => import('./icons/chart.svg'));
 *
 * // Use in registry
 * export const icons = {
 *   'app/chart': chartIcon,
 * } as const;
 * ```
 */
export const createLazyIcon = (
  importFn: () => Promise<{default: IconComponent}>
): LazyIconEntry => {
  let preloadPromise: Promise<{default: IconComponent}> | null = null;

  const Component = lazy(importFn);

  const preload = () => {
    if (!preloadPromise) {
      preloadPromise = importFn();
    }
    return preloadPromise;
  };

  return {Component, preload};
};
